import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { blogPosts } from "@/mocks/blog-posts";
import { allArticles } from "@/mocks/blog-articles";

const siteOrigin = import.meta.env.VITE_SITE_URL || "https://acbuy.team";

const breadcrumbData = (slug: string, title: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteOrigin}/` },
    { "@type": "ListItem", "position": 2, "name": title, "item": `${siteOrigin}/${slug}` },
  ],
});

export default function BlogArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? allArticles[slug] : undefined;
  const postMeta = blogPosts.find((p) => p.id === slug);

  if (!article || !postMeta) {
    return (
      <>
        <Navbar forceSolid />
        <main className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">Article not found</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-primary-600 hover:underline cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": postMeta.excerpt,
    "image": postMeta.imageUrl,
    "author": { "@type": "Organization", "name": "ACBuy Team", "url": siteOrigin },
    "datePublished": postMeta.date,
    "dateModified": postMeta.date,
    "publisher": {
      "@type": "Organization",
      "name": "ACBuy Team",
      "url": siteOrigin,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteOrigin}/${slug}`
    }
  };

  const relatedArticles = blogPosts.filter((p) => p.id !== slug).slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData(slug, article.title)) }}
      />

      <Navbar forceSolid />

      <main>
        {/* Article Header */}
        <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
                <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
              </nav>

              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wide mb-4 whitespace-nowrap">
                {article.badge}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                {article.title}
              </h1>

              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: article.intro }} />

              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span>Published: {article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>By ACBuy Team</span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="pb-10 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden aspect-[21/9] bg-background-100">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - ACBuy Spreadsheet`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              {article.sections.map((section, sIdx) => {
                if (section.type === "heading") {
                  return (
                    <section key={sIdx} className="mb-10">
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                        {section.heading}
                      </h2>
                    </section>
                  );
                }

                if (section.type === "text" && section.content) {
                  return (
                    <section key={sIdx} className="mb-6">
                      <p className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base" dangerouslySetInnerHTML={{ __html: section.content }} />
                    </section>
                  );
                }

                if (section.type === "list" && section.items) {
                  return (
                    <section key={sIdx} className="mb-8">
                      <div className="space-y-4">
                        {section.items.map((item, idx) => (
                          <div key={idx} className="flex gap-4">
                            <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                              {idx + 1}
                            </div>
                            <p className="text-sm text-foreground-600 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                if (section.type === "table" && section.tableHeaders && section.tableRows) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <div className="overflow-x-auto rounded-2xl border border-background-200">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-background-100">
                              {section.tableHeaders.map((h, idx) => (
                                <th key={idx} className={`text-left py-3.5 px-5 text-sm font-semibold ${idx === 1 ? "text-primary-700" : "text-foreground-700"}`}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.tableRows.map((row, idx) => (
                              <tr key={idx} className="border-t border-background-200 hover:bg-background-100/50 transition-colors">
                                {row.map((cell, cIdx) => (
                                  <td key={cIdx} className={`py-3 px-5 text-sm ${cIdx === 1 ? "text-primary-700 font-medium" : "text-foreground-600"}`}>
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {section.note && (
                        <div className="mt-5 p-4 rounded-xl bg-primary-50 border border-primary-200">
                          <p className="text-sm text-primary-800 leading-relaxed">{section.note}</p>
                        </div>
                      )}
                    </section>
                  );
                }

                if (section.type === "tips" && section.tips) {
                  return (
                    <section key={sIdx} className="mb-8">
                      <div className="space-y-3">
                        {section.tips.map((tip, idx) => (
                          <div key={idx} className="flex gap-4 p-4 rounded-xl bg-background-100">
                            <div className="w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                              {idx + 1}
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-foreground-900 mb-1">{tip.title}</h4>
                              <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                if (section.type === "faqList" && section.faqs) {
                  return (
                    <section key={sIdx} className="mb-8">
                      <div className="space-y-3">
                        {section.faqs.map((faq, idx) => (
                          <div key={idx} className="p-5 rounded-2xl bg-background-100">
                            <h4 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }

                if (section.type === "cta") {
                  return (
                    <section key={sIdx} className="text-center py-8 px-6 rounded-2xl bg-primary-50 border border-primary-200 mb-8">
                      {section.heading && (
                        <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                          {section.heading}
                        </h3>
                      )}
                      {section.content && (
                        <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">{section.content}</p>
                      )}
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <a
                          href={mainSiteUrl}
                          target="_blank"
                          rel="noopener nofollow"
                          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Access ACBuy Spreadsheet
                          <i className="ri-arrow-right-line"></i>
                        </a>
                        <Link
                          to="/"
                          className="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-foreground-300 text-foreground-800 text-sm font-semibold hover:border-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Back to Home
                          <i className="ri-home-4-line"></i>
                        </Link>
                      </div>
                    </section>
                  );
                }

                return null;
              })}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                More ACBuy Spreadsheet Resources
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/${related.id}`}
                    className="group block p-5 rounded-2xl bg-background-50 hover:bg-background-200/30 transition-colors duration-300 cursor-pointer"
                  >
                    <span className="inline-block px-2.5 py-1 rounded-lg bg-accent-100 text-accent-800 text-xs font-medium mb-2 whitespace-nowrap">
                      {related.category}
                    </span>
                    <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors mb-2 line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{related.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}